export const AVITO_AUTOLOAD_UI_STATES = Object.freeze({
	NO_ACCOUNT: 'NO_ACCOUNT',
	ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
	ACCOUNT_NO_LISTINGS: 'ACCOUNT_NO_LISTINGS',
	LISTINGS_NEED_FIX: 'LISTINGS_NEED_FIX',
	FIRST_HOSTED_SETUP: 'FIRST_HOSTED_SETUP',
	READY_TO_CONNECT: 'READY_TO_CONNECT',
	CONNECTED_CLEAN: 'CONNECTED_CLEAN',
	SYNCING: 'SYNCING',
	SYNC_FAILED_WITH_LKG: 'SYNC_FAILED_WITH_LKG',
	EMPTY_WITH_LKG: 'EMPTY_WITH_LKG',
	TOKEN_ROTATION: 'TOKEN_ROTATION',
})

function pluralizeListings(count) {
	const value = Math.abs(Number(count)) % 100
	const last = value % 10
	if (value > 10 && value < 20) return 'объявлений'
	if (last === 1) return 'объявление'
	if (last > 1 && last < 5) return 'объявления'
	return 'объявлений'
}

function action(id, label) {
	return { id, label }
}

export function deriveAvitoAutoloadViewModel({
	accounts = [],
	selectedAccount = null,
	autoload = { count: 0, listings: [] },
	hostedFeed = null,
	syncing = false,
	showConnectionStep = false,
	rotating = false,
} = {}) {
	const count = Number(autoload?.count || 0)
	const listings = Array.isArray(autoload?.listings) ? autoload.listings : []
	const invalidListings = listings.filter((listing) => listing.health !== 'READY')
	const common = { count, invalidListings }

	if (rotating) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.TOKEN_ROTATION,
			severity: 'warning',
			headline: 'Создать новую ссылку?',
			explanation:
				'Старая ссылка перестанет работать. Новую ссылку потребуется заменить в настройках Авито.',
			primaryAction: action('confirm-rotation', 'Создать новую ссылку'),
			secondaryAction: action('cancel-rotation', 'Отмена'),
		}
	}

	if (accounts.length === 0) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.NO_ACCOUNT,
			severity: 'info',
			headline: 'Сначала добавьте аккаунт Авито',
			explanation:
				'Аккаунт нужен, чтобы настроить отдельную автозагрузку объявлений.',
			primaryAction: action('add-account', 'Добавить аккаунт'),
			secondaryAction: null,
		}
	}

	if (selectedAccount?.status === 'disabled') {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.ACCOUNT_DISABLED,
			severity: 'warning',
			headline: 'Аккаунт отключён',
			explanation:
				'Включите аккаунт, чтобы Reldo снова мог обновлять данные для Авито.',
			primaryAction: action('manage-account', 'Настроить аккаунт'),
			secondaryAction: null,
		}
	}

	if (syncing || (hostedFeed?.state === 'DIRTY' && !hostedFeed?.lastError)) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.SYNCING,
			severity: 'info',
			headline: 'Обновляем данные для Авито…',
			explanation: 'Последние изменения ещё обрабатываются.',
			primaryAction: null,
			secondaryAction: action('open-settings', 'Настройки автозагрузки'),
		}
	}

	if (count === 0 && hostedFeed?.ready) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.EMPTY_WITH_LKG,
			severity: 'warning',
			headline: 'В автозагрузке больше нет объявлений',
			explanation:
				'Reldo не отправил пустые данные, чтобы случайно не снять объявления с Авито.',
			primaryAction: action('add-listings', 'Добавить объявления'),
			secondaryAction: action('open-settings', 'Настройки автозагрузки'),
		}
	}

	if (hostedFeed?.state === 'FAILED') {
		const hasPreviousData = hostedFeed.ready === true
		return {
			...common,
			state: hasPreviousData
				? AVITO_AUTOLOAD_UI_STATES.SYNC_FAILED_WITH_LKG
				: AVITO_AUTOLOAD_UI_STATES.FIRST_HOSTED_SETUP,
			severity: 'warning',
			headline: hasPreviousData
				? 'Не удалось применить последние изменения'
				: 'Не удалось подготовить данные для Авито',
			explanation: hasPreviousData
				? 'Автозагрузка продолжает работать с предыдущими данными.'
				: 'Объявления сохранены. Повторите подготовку ссылки.',
			primaryAction: action('retry', 'Повторить'),
			secondaryAction:
				invalidListings.length > 0
					? action('view-problem', 'Посмотреть проблему')
					: action('open-settings', 'Настройки автозагрузки'),
		}
	}

	if (count === 0) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.ACCOUNT_NO_LISTINGS,
			severity: 'info',
			headline: 'Добавьте объявления',
			explanation:
				'Reldo проверит их и автоматически подготовит данные для Авито.',
			primaryAction: action('add-listings', 'Добавить объявления'),
			secondaryAction: action('open-settings', 'Настройки автозагрузки'),
		}
	}

	if (invalidListings.length > 0) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.LISTINGS_NEED_FIX,
			severity: 'warning',
			headline: `Нужно исправить ${invalidListings.length} ${pluralizeListings(invalidListings.length)}`,
			explanation: 'Откройте объявление и заполните недостающие данные.',
			primaryAction: action('fix-first-listing', 'Исправить'),
			secondaryAction: action('add-listings', 'Добавить объявления'),
		}
	}

	if (!hostedFeed?.configured || !hostedFeed?.ready) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.FIRST_HOSTED_SETUP,
			severity: 'info',
			headline: 'Подготовьте ссылку для Авито',
			explanation:
				'Reldo проверит объявления, подготовит данные и создаст постоянную ссылку.',
			primaryAction: action('prepare', 'Подготовить ссылку'),
			secondaryAction: action('open-guide', 'Как настроить в Авито'),
		}
	}

	if (showConnectionStep) {
		return {
			...common,
			state: AVITO_AUTOLOAD_UI_STATES.READY_TO_CONNECT,
			severity: 'success',
			headline: 'Подключите Reldo к Авито',
			explanation:
				'Скопируйте ссылку и один раз укажите её в настройках автозагрузки Авито.',
			primaryAction: action('copy-url', 'Скопировать ссылку'),
			secondaryAction: action('open-guide', 'Как настроить в Авито'),
		}
	}

	return {
		...common,
		state: AVITO_AUTOLOAD_UI_STATES.CONNECTED_CLEAN,
		severity: 'success',
		headline: 'Всё в порядке',
		explanation: `${count} ${pluralizeListings(count)} передаются в Авито.`,
		primaryAction: action('add-listings', 'Добавить объявления'),
		secondaryAction: action('open-settings', 'Настройки автозагрузки'),
	}
}
